import { AfterViewInit, Component, DestroyRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { debounceTime, finalize } from 'rxjs/operators';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ClipboardModule } from 'ngx-clipboard';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DefaultDatesComponent, FilterColumnComponent, FilterSortingComponent, LeftColumnComponent, ListPaginationComponent } from '@parashift/shared/components';
import { FilterConfig, Webhook, WebhookEvent, ListFilter } from '@parashift/shared/models';
import { ErrorResponse, JsonApiQueryData, WebhookService, WebhookEventService, ListFilterService, Logger } from '@parashift/shared/services';
import { PdcDatePipe, IterateObjectPipe } from '@parashift/shared/pipes';
import { DateFromNowDirective } from '@parashift/shared/directives';
import { HeaderActionStore } from '@parashift/shared/stores';

@Component({
  selector: 'webhooks-detail',
  templateUrl: './webhooks-detail.component.html',
  imports: [
    ClipboardModule,
    DateFromNowDirective,
    DefaultDatesComponent,
    FaIconComponent,
    FilterColumnComponent,
    FilterSortingComponent,
    IterateObjectPipe,
    LeftColumnComponent,
    ListPaginationComponent,
    PdcDatePipe,
    TooltipModule
]
})
export class WebhooksDetailComponent implements AfterViewInit, OnInit {
  private webhookService = inject(WebhookService);
  private webhookEventService = inject(WebhookEventService);
  private listFilterService = inject(ListFilterService);
  private headerActionStore = inject(HeaderActionStore);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private logger = inject(Logger);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  @ViewChild(FilterColumnComponent) filterComponent: FilterColumnComponent;
  @Input() filterColumnConfig: FilterConfig;

  webhook: Webhook;
  error: ErrorResponse;
  events: JsonApiQueryData<WebhookEvent>;
  listFilter: ListFilter;
  isCollapsed = {};
  loading = false;

  private tenant_id: number;
  private webhookId: string;

  constructor() {
    const destroyRef = this.destroyRef;
    destroyRef.onDestroy(() => this.headerActionStore.destroy());
  }

  ngOnInit() {
    this.headerActionStore.addActions({ backButton: { dispatch: () => this.goToListView() }});

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.tenant_id = params.get('tenant_id') ? Number(params.get('tenant_id')) : undefined;
      this.webhookId = params.get('id');
      this.initListFilter();
      this.getWebhook(this.webhookId);
      this.getEvents(this.webhookId);
      this.headerActionStore.addActions({ editButton: { dispatch: () => this.webhook && this.editWebhook(this.webhook.id) }});
    });
  }

  initListFilter() {
    this.listFilter = this.listFilterService.getStoredFilters(
      this.filterColumnConfig.meta.identifier,
      {
        stats: { total: ['count'] },
        include: 'logs',
        sorting: { direction: '-', field: 'created_at' }
      },
      true
    );
  }

  ngAfterViewInit() {
    this.filterComponent.$filterParams
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400)
      )
      .subscribe(listFilter => {
        this.listFilter = listFilter as ListFilter;
        this.getEvents(this.webhookId);
      });
  }

  private getWebhook(id: string) {
    const listFilter = new ListFilter({ include: 'topics' });

    this.webhookService.findRecord(id, listFilter, this.tenant_id).subscribe({
      next: (webhook: Webhook) => {
        this.webhook = webhook;
      },
      error: (error: ErrorResponse) => {
        this.error = error;
        this.logger.log('webhooks-detail-component', 'WEBHOOK Error', error);
      }
    });
  }

  private getEvents(webhookId: string) {
    this.loading = true;
    this.webhookEventService.getEventsByWebhook(webhookId, this.listFilter, this.tenant_id)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (events: JsonApiQueryData<WebhookEvent>) => this.events = events,
        error: (error: ErrorResponse) => this.logger.log('webhooks-detail-component', 'EVENTS Error', error),
      });
  }

  private goToListView() {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  private editWebhook(id: string) {
    this.router.navigate(['../../', id, 'edit'], { relativeTo: this.route });
  }
}
